import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

// Модальное окно через портал в body: подложка с блюром, панель-card,
// Esc и клик по подложке закрывают (второе отключается closeOnBackdrop),
// скролл страницы заблокирован, фокус — на первое поле панели.

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export function Modal({
  open,
  onClose,
  title,
  size = "md",
  footer,
  closeOnBackdrop = true,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: keyof typeof SIZES;
  /** Низ панели: кнопки. Выравниваются вправо. */
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Автофокус: первое поле ввода, иначе сама панель (чтобы Esc ловился).
    const el = panelRef.current;
    const first = el?.querySelector<HTMLElement>(
      "input:not([type=hidden]),textarea,select,[autofocus]",
    );
    (first ?? el)?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in">
      <div
        className="absolute inset-0 bg-ink-950/40 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={cn(
          "relative card w-full bg-white dark:bg-ink-900 max-h-[90vh] flex flex-col outline-none animate-slide-up",
          SIZES[size],
          className,
        )}
      >
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
          <div className="font-bold">{title}</div>
          <button
            type="button"
            className="btn px-2"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 pb-5 overflow-auto min-h-0">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-ink-200/70 dark:border-ink-800/70">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
